/**
 * Feature Flags Admin Page (Staging / Dev only)
 *
 * Allows toggling feature flags at runtime. Overrides persist in localStorage.
 * Only available when REACT_APP_ENABLE_FEATURE_FLAGS_UI=true or NODE_ENV=development.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainPageLayout from '../../components/common/MainPageLayout';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { FEATURE_FLAGS } from '../../constants/featureFlags';
import { RotateCcw } from 'lucide-react';

/** Labels for each flag (key from FEATURE_FLAGS). Add entries here when adding new flags. */
const FLAG_LABELS = Object.fromEntries(
  Object.keys(FEATURE_FLAGS).map((key) => [
    key,
    { title: key, description: '' },
  ]),
);

export const FeatureFlagsPage = () => {
  const navigate = useNavigate();
  const { isEnabled, setFlag, resetOverrides } = useFeatureFlags();

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true, hideMenu: true }}>
      <div className="max-w-[720px] mx-auto px-6 py-8">
        <h1 className="font-['Roboto:Bold',sans-serif] font-bold text-[28px] text-black mb-2">
          Feature Flags
        </h1>
        <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-gray-600 mb-8">
          Toggle features for staging. Changes are saved in this browser (localStorage) and
          persist until you reset. Reload the app after changing.
        </p>

        <div className="space-y-6 mb-10">
          {Object.entries(FLAG_LABELS).map(([key, { title, description }]) => (
            <div
              key={key}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[18px] text-black mb-1">
                    {title}
                  </h2>
                  <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 leading-relaxed">
                    {description}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isEnabled(key)}
                  onClick={() => setFlag(key, !isEnabled(key))}
                  className={`
                    relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 transition-colors
                    focus:outline-none focus:ring-2 focus:ring-[#61b8e7] focus:ring-offset-2
                    ${isEnabled(key) ? 'bg-[#61b8e7] border-[#61b8e7]' : 'bg-gray-200 border-gray-300'}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition
                      ${isEnabled(key) ? 'translate-x-5' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#61b8e7] text-white rounded-lg hover:bg-[#4da3d1] font-['Roboto:Medium',sans-serif] text-[14px]"
          >
            Go to login
          </button>
          <button
            type="button"
            onClick={resetOverrides}
            className="flex items-center gap-2 px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-['Roboto:Medium',sans-serif] text-[14px]"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to defaults (from env)
          </button>
        </div>
      </div>
    </MainPageLayout>
  );
};
